import React, { useEffect, useMemo, useState } from "react";
import { UserPlus, RefreshCw, ArrowUpRight, ArrowDownRight, Minus, Smartphone, Monitor, Download, HelpCircle, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useGuard } from "@/lib/guards";

// Admin › Supervision › Inscriptions par jour (lot 625) — nombre de nouveaux membres par jour et par plateforme
// (android / ios / web), variation vs la veille, totaux de la période et depuis le début.
const PERIODS = [7, 30, 90];
const PLAT = [
  { key: "android", label: "Android", color: "var(--yalola-ok)", Icon: Smartphone },
  { key: "ios", label: "iOS", color: "var(--yalola-info)", Icon: Smartphone },
  { key: "web", label: "Web", color: "#C2410C", Icon: Monitor },
  { key: "unknown", label: "Inconnu", color: "#9CA3AF", Icon: HelpCircle },
];
// Lot 764 — pagination mois par mois (« Mois » : ‹ octobre 2026 › ; mois en cours jusqu'à aujourd'hui, comparé au mois précédent)
export const fmtMonth = (m) => { try { const s = new Date(`${m}-01T00:00:00Z`).toLocaleDateString("fr-FR", { month: "long", year: "numeric", timeZone: "UTC" }); return s.charAt(0).toUpperCase() + s.slice(1); } catch { return m; } };
export const currentMonth = () => new Date().toISOString().slice(0, 7);
const fmtDay = (d) => { try { return new Date(`${d}T00:00:00Z`).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", timeZone: "UTC" }); } catch { return d; } };

const Delta = ({ value, pct = null, testId }) => {
  if (value == null) return <span style={{ color: "var(--yalola-text-muted)" }} data-testid={testId}>—</span>;
  const up = value > 0, down = value < 0;
  const color = up ? "var(--yalola-ok)" : down ? "var(--yalola-danger)" : "var(--yalola-text-muted)";
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums" style={{ color }} data-testid={testId} data-trend={up ? "up" : down ? "down" : "flat"}>
      <Icon size={13} /> {value > 0 ? `+${value}` : value}{pct != null ? ` (${pct > 0 ? "+" : ""}${pct} %)` : ""}
    </span>
  );
};

const StatCard = ({ label, value, hint, color, testId }) => (
  <div className="rounded-2xl border p-4" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid={testId}>
    <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
    <div className="text-2xl font-bold mt-1 tabular-nums" style={{ color: color || "inherit" }}>{value}</div>
    {hint && <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{hint}</div>}
  </div>
);

const Bars = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <div className="flex items-end gap-[3px] h-28 px-1" data-testid="signups-chart" aria-hidden="true">
      {rows.map((r) => (
        <div key={r.date} className="flex-1 flex flex-col-reverse rounded-t-sm overflow-hidden" style={{ height: `${Math.max(2, (r.total / max) * 100)}%`, background: r.total ? "transparent" : "var(--yalola-border)" }} title={`${fmtDay(r.date)} · ${r.total}`}>
          {PLAT.map((p) => r[p.key] > 0 && <div key={p.key} style={{ background: p.color, flex: r[p.key] }} />)}
        </div>
      ))}
    </div>
  );
};

const exportCsv = (data) => {
  const lines = [["date", "total", "variation", "android", "ios", "web", "inconnu"].join(";")];
  data.rows.forEach((r) => lines.push([r.date, r.total, r.delta ?? "", r.android, r.ios, r.web, r.unknown].join(";")));
  const blob = new Blob([`\uFEFF${lines.join("\n")}`], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = `inscriptions_${data.from}_${data.to}.csv`; a.click();
  URL.revokeObjectURL(a.href);
};

export const SignupsPanel = () => {
  const monthPagerOn = useGuard("signups_month_pager"); // lot 764
  const [days, setDays] = useState(30);
  const [month, setMonth] = useState(null); // "YYYY-MM" → mode mois ; null → N derniers jours
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (d = days, m = month) => {
    setLoading(true);
    try { const { data: res } = await api.get(m ? `/admin/stats/signups?month=${m}` : `/admin/stats/signups?days=${d}`); setData(res); }
    catch (e) { toast.error(e?.friendlyMessage || "Statistiques indisponibles"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(days, month); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days, month]);
  const rowsDesc = useMemo(() => (data ? [...data.rows].reverse() : []), [data]);
  const p = data?.period, all = data?.all_time;
  const isMonth = !!month && data?.mode === "month";
  const periodLabel = isMonth ? fmtMonth(data.month) : `${data?.days ?? days} j`;
  const vsLabel = isMonth ? (data.prev_month ? `vs ${fmtMonth(data.prev_month)}` : "vs mois précédent") : `vs ${data?.days} j précédents`;
  return (
    <div className="space-y-4" data-testid="signups-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><UserPlus size={18} /> Inscriptions par jour</h2>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Nouveaux membres (bots exclus) par jour et par plateforme · jours en UTC · variation vs la veille.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border p-0.5" style={{ borderColor: "var(--yalola-border)" }}>
            {PERIODS.map((n) => (
              <button key={n} type="button" onClick={() => { setMonth(null); setDays(n); }} className="px-3 py-1 rounded-full text-xs font-semibold" style={!month && days === n ? { background: "var(--yalola-primary)", color: "#fff" } : { background: "transparent", color: "var(--yalola-text-muted)" }} data-testid={`signups-period-${n}`} aria-pressed={!month && days === n}>{n} j</button>
            ))}
            {monthPagerOn && (
              <button type="button" onClick={() => setMonth((m) => m || currentMonth())} className="px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1" style={month ? { background: "var(--yalola-primary)", color: "#fff" } : { background: "transparent", color: "var(--yalola-text-muted)" }} data-testid="signups-period-month" aria-pressed={!!month}><CalendarDays size={12} /> Mois</button>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} data-testid="signups-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></Button>
          <Button variant="outline" size="sm" onClick={() => data && exportCsv(data)} disabled={!data} data-testid="signups-export"><Download size={14} className="mr-1" /> CSV</Button>
        </div>
      </div>

      {month && (
        <div className="flex items-center justify-between gap-2 rounded-2xl border px-3 py-2" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid="signups-month-nav">
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => data?.prev_month && setMonth(data.prev_month)} disabled={loading || !data?.prev_month} title="Mois précédent" data-testid="signups-month-prev"><ChevronLeft size={16} /></Button>
          <div className="text-center min-w-0">
            <div className="font-bold" data-testid="signups-month-label">{fmtMonth(month)}</div>
            <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
              {data?.mode === "month" ? (data.complete ? `${data.days} jours · mois complet` : `${data.days} jour${data.days > 1 ? "s" : ""} · mois en cours (jusqu'à aujourd'hui)`) : "…"}
              {data?.first_month ? ` · depuis ${fmtMonth(data.first_month)}` : ""}
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => data?.next_month && setMonth(data.next_month)} disabled={loading || !data?.next_month} title="Mois suivant" data-testid="signups-month-next"><ChevronRight size={16} /></Button>
        </div>
      )}

      {p && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label={`Inscrits · ${periodLabel}`} value={p.total} color="var(--yalola-primary)" testId="signups-period-total"
            hint={<span className="inline-flex items-center gap-1">{vsLabel} <Delta value={p.delta} pct={p.delta_pct} testId="signups-period-delta" /></span>} />
          {PLAT.slice(0, 3).map((pl) => <StatCard key={pl.key} label={pl.label} value={p[pl.key]} color={pl.color} testId={`signups-period-${pl.key}`} hint={p.total ? `${Math.round((p[pl.key] / p.total) * 100)} % · ${all[pl.key]} au total` : `${all[pl.key]} au total`} />)}
          <StatCard label="Total membres" value={all.total} testId="signups-all-total" hint={`${p.avg_per_day}/jour en moyenne${all.unknown ? ` · ${all.unknown} plateforme inconnue` : ""}`} />
        </div>
      )}

      {data && (
        <div className="rounded-2xl border p-4 space-y-3" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }}>
          <Bars rows={data.rows} />
          <div className="flex flex-wrap gap-3 text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
            {PLAT.map((pl) => <span key={pl.key} className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: pl.color }} /> {pl.label}</span>)}
          </div>
        </div>
      )}

      {data?.retention && (
        <div className="rounded-2xl border p-4" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid="signups-retention">
          <div className="font-bold text-sm">Rétention · encore actifs après 7 et 30 jours</div>
          <p className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>Parmi les inscrits de la période ayant au moins 7 j (ou 30 j) d'ancienneté : part de ceux revenus dans l'app ou sur le site à partir du 7ᵉ (ou 30ᵉ) jour.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {["7", "30"].map((k) => {
              const r = data.retention[k];
              return (
                <div key={k} className="rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }} data-testid={`signups-retention-${k}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-xs font-semibold uppercase" style={{ color: "var(--yalola-text-muted)" }}>Après {k} jours</div>
                    <div className="text-xl font-bold tabular-nums" style={{ color: r.pct == null ? "var(--yalola-text-muted)" : r.pct >= 40 ? "var(--yalola-ok)" : r.pct >= 20 ? "#C2410C" : "var(--yalola-danger)" }} data-testid={`signups-retention-${k}-pct`}>{r.pct == null ? "—" : `${r.pct} %`}</div>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{r.active} actifs sur {r.cohort} inscrits éligibles</div>
                  <div className="h-1.5 rounded-full mt-2 overflow-hidden" style={{ background: "var(--yalola-border)" }}><div className="h-full rounded-full" style={{ width: `${r.pct || 0}%`, background: "var(--yalola-primary)" }} /></div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
                    {PLAT.filter((pl) => r.by_platform?.[pl.key]?.cohort).map((pl) => {
                      const b = r.by_platform[pl.key];
                      return <span key={pl.key} className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm" style={{ background: pl.color }} />{pl.label} {b.active}/{b.cohort} ({Math.round((b.active / b.cohort) * 100)} %)</span>;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data && (
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }}>
          <table className="w-full text-sm" data-testid="signups-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-left" style={{ color: "var(--yalola-text-muted)", background: "var(--yalola-bg)" }}>
                <th className="px-3 py-2 font-semibold">Jour</th>
                <th className="px-3 py-2 font-semibold text-right">Inscrits</th>
                <th className="px-3 py-2 font-semibold text-right">Variation</th>
                {PLAT.map((pl) => <th key={pl.key} className="px-3 py-2 font-semibold text-right hidden sm:table-cell">{pl.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rowsDesc.map((r) => (
                <tr key={r.date} className="border-t" style={{ borderColor: "var(--yalola-border)", opacity: r.total ? 1 : 0.6 }} data-testid={`signups-row-${r.date}`}>
                  <td className="px-3 py-2 whitespace-nowrap capitalize">{fmtDay(r.date)}</td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums" data-testid={`signups-total-${r.date}`}>{r.total}</td>
                  <td className="px-3 py-2 text-right"><Delta value={r.delta} testId={`signups-delta-${r.date}`} /></td>
                  {PLAT.map((pl) => <td key={pl.key} className="px-3 py-2 text-right tabular-nums hidden sm:table-cell" style={{ color: r[pl.key] ? pl.color : "var(--yalola-text-muted)" }}>{r[pl.key]}</td>)}
                </tr>
              ))}
            </tbody>
            {p && (
              <tfoot>
                <tr className="border-t font-bold" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-bg)" }} data-testid="signups-footer">
                  <td className="px-3 py-2">Total {periodLabel}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.total}</td>
                  <td className="px-3 py-2 text-right"><Delta value={p.delta} pct={p.delta_pct} /></td>
                  {PLAT.map((pl) => <td key={pl.key} className="px-3 py-2 text-right tabular-nums hidden sm:table-cell">{p[pl.key]}</td>)}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};
